import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { defineMessages } from 'react-intl';

import { HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID } from '../../../contracts/heidi-2';
import {
  getHeidi2Ports,
  heidi2EvidenceTourStepAtom,
  HEIDI_2_EVIDENCE_TOUR_TOTAL_STEPS,
  type Heidi2EvidenceTourStep,
} from '../../../data/heidi-2';

export const messages = defineMessages({
  homeTitle: {
    defaultMessage: 'Evidence has moved',
    id: 'Vc5ogDAFJk',
    description: 'Title of the Heidi II spotlight pointing at the Home rail item',
  },
  homeDescription: {
    defaultMessage:
      'Use Home to get answers and support decisions with full-text journals',
    id: 'i5S6UH6bQX',
    description: 'Body of the Heidi II spotlight pointing at the Home rail item',
  },
  next: {
    defaultMessage: 'Next',
    id: 'gQEdJOMYg3',
    description: 'Button that advances the Heidi II Evidence tour to the next step',
  },
  recentsTitle: {
    defaultMessage: 'Your chats and sessions',
    id: 'BszzXH3gNH',
    description: 'Title of the Heidi II spotlight pointing at Recents in the Home panel',
  },
  recentsDescription: {
    defaultMessage:
      'Jump back into recent Evidence chats and Scribe sessions from the side panel.',
    id: 'srUv173TUC',
    description: 'Body of the Heidi II spotlight pointing at Recents in the Home panel',
  },
  addMenuTitle: {
    defaultMessage: 'Dedicated Evidence look-up',
    id: 'Lrnbk/9WHU',
    description:
      'Title of the Heidi II spotlight pointing at Evidence only in the composer + menu',
  },
  addMenuDescription: {
    defaultMessage: 'Get faster answers to your clinical questions',
    id: '6YNANoNsnx',
    description:
      'Body of the Heidi II spotlight pointing at Evidence only in the composer + menu',
  },
  gotIt: {
    defaultMessage: 'Got it',
    id: 'K09X9L3jZ3',
    description: 'Button on the last Heidi II Evidence tour spotlight that ends the tour',
  },
  progress: {
    defaultMessage: '{current}/{total}',
    id: 'RB133Eb5ay',
    description: 'Step counter shown in the Heidi II Evidence tour spotlight footer',
  },
});

export const useTrackTourStepViewed = (
  isShowing: boolean,
  step: Heidi2EvidenceTourStep
) => {
  const trackStepViewed = getHeidi2Ports().trackHeidi2EvidenceTourStepViewed;
  const trackedRef = useRef(false);
  useEffect(() => {
    if (!isShowing || trackedRef.current) return;
    trackedRef.current = true;
    trackStepViewed({ step, totalSteps: HEIDI_2_EVIDENCE_TOUR_TOTAL_STEPS });
  }, [isShowing, step, trackStepViewed]);
};

/**
 * Whether step 1 is showing. Recents keeps its heading up for it even with no
 * sessions or chats yet, so the tour never skips from the rail to the + menu.
 */
export const useIsHeidi2EvidenceTourOnRecents = () => {
  const isOpen =
    getHeidi2Ports().useCurrentQueuedModalId() ===
    HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID;
  const step = useAtomValue(heidi2EvidenceTourStepAtom);
  return isOpen && step === 1;
};

export const trackTourDismiss = (step: Heidi2EvidenceTourStep) => {
  getHeidi2Ports().trackButtonClicked({
    screen_name: ScreenName.Heidi2EvidenceTourSpotlight,
    action: ButtonAction.Heidi2EvidenceTourDismiss,
    label: `step_${step.toString()}`,
  });
};
